/**
 * Credential + step-field insert syntax — sl-fpi2.3
 *
 * Formats an insertable reference as the literal text that ends up in
 * the editor. Under sl-fpi2.3 there is exactly ONE syntax — the double-
 * mustache template form — shared by every editor in the Workflow
 * Builder (AI prompts, HTTP URL/headers/body, Code editor source,
 * Service Node inputs). The Code node's runner resolves `{{...}}`
 * placeholders via pre-rendering (`resolveCodeTemplate`) BEFORE sandbox
 * execution, so the sandbox sees the same inline values the template
 * syntax promises.
 *
 * History: the old `code-js` / `code-python` variants emitted
 * `input['credentials']['<field>']` / `input['<stepId>']['<fieldId>']`
 * because the pre-sl-fpi2.2 Code executor shipped decrypted credentials
 * and step outputs as bundle keys on the `input` object. That bundle
 * is gone — the unified template syntax replaces it entirely.
 *
 * Multi-credential contract: credentials are keyed by credential id,
 * so `formatCredentialInsertText` takes the owning credential id and
 * emits `{{credential.<id>.<field>}}`. The runner's resolver matches
 * the same 3-part path. See `template-resolver.ts` in the runner.
 */

/**
 * Format a credential reference for insertion into the editor.
 *
 * Rejects ids/fields that would break the template grammar (`{`, `}`,
 * whitespace, `.`) so a malformed credential record cannot produce an
 * insert string that the runner's regex would mis-parse or split across
 * a different credential id. Defense in depth — the backend still owns
 * the authoritative RBAC scan via `extractCredentialRefs`.
 *
 * @param credentialId — workspace credential id that owns the field
 * @param field         — credential field name (e.g. "apiKey", "token")
 */
const CREDENTIAL_TOKEN_INVALID = /[{}\s.]/;

export function formatCredentialInsertText(
  credentialId: string,
  field: string
): string {
  if (!credentialId || CREDENTIAL_TOKEN_INVALID.test(credentialId)) {
    throw new Error(
      `Invalid credential id for template insert: ${JSON.stringify(credentialId)}`
    );
  }
  if (!field || CREDENTIAL_TOKEN_INVALID.test(field)) {
    throw new Error(
      `Invalid credential field for template insert: ${JSON.stringify(field)}`
    );
  }
  return `{{credential.${credentialId}.${field}}}`;
}

/**
 * Format an upstream step-field reference for insertion into the editor.
 *
 * CRITICAL — matches the runner's wire format, not the human-friendly
 * field name: the runner's `wrapWithFieldIds` in `workflow-engine.ts`
 * stores every non-Start/End step output as `Record<fieldId, value>`
 * where `fieldId` is exactly the id produced by
 * `fixedFieldId(stepId, fieldName) = field-<stepId>-<fieldName>`.
 * `resolveVariable` then does a direct `navigatePath(output, [fieldId])`
 * — so the template path MUST carry `fieldId`, not `fieldName`. Start
 * step outputs bypass the wrapper and use the schema's raw field id
 * directly; callers pass that id here unchanged.
 *
 * @param stepId  — id of the upstream step
 * @param fieldId — field id as reported by `StepOutputShapeProvider.getShape`
 */
export function formatStepFieldInsertText(
  stepId: string,
  fieldId: string
): string {
  return `{{steps.${stepId}.${fieldId}}}`;
}

export function formatInputFieldInsertText(fieldId: string): string {
  if (!fieldId || CREDENTIAL_TOKEN_INVALID.test(fieldId)) {
    throw new Error(
      `Invalid input field id for template insert: ${JSON.stringify(fieldId)}`
    );
  }
  return `{{input.${fieldId}}}`;
}
